import React, {useEffect, useMemo, useState} from 'react';
import { getLastWeekDateTimeRange } from '../../../utils/dateTimeUtils';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';

import {getUserInfo} from "../../../utils/auth";
import {useHashState} from 'utils/hashState';
import {ParcelColumns} from "../../delivery/parcels/components/ParcelColumns";
import {OrderType} from "../../../graphql/types/OrderType";
import CentralParcelDetailDrawer from "../../central/parcels/components/CentralParcelDetailDrawer";
import {useWmsParcelQuery} from "./hooks/useWmsParcelQuery";
import WmsParcelFilter from "./components/WmsParcelFilter";
import { WMSOrderFiltersType, WMSParcelInitialValue } from './graphql/type/WMSOrderFiltersType';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { normalizeRequiredFields } from './utils/buildParcelQueryFilters';
import { GET_ORDER_LISTING_FOR_WMS } from './graphql/queries/getOrderListingForWMS';
import {
    hasTrackingOrOrderNumber,
    ParcelEmptyGuide,
    PartialNotFoundBar,
    useNotFoundTrackingNumbers,
    useParcelQueryRangeDays,
} from "../../../components/parcelQueryRange";

const WmsParcelsPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const [detailVisible, setDetailVisible] = useState(false);
    const [orderId, setOrderId] = useState<string>('');
    // 创建不包含currentWarehouseId和sortingCode的初始值，这些值从全局状态获取
    const createInitialFilters = () => {
        const { currentWarehouseId, sortingCode, ...otherFilters } = WMSParcelInitialValue;
        return otherFilters;
    };

    const [tempFilters, setTempFilters] = useHashState(createInitialFilters());
    const [filters, setFilters] = useHashState(createInitialFilters());
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    // 表格当前页受控选中 key
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    // 跨页保留的选中 key 集合
    const [lockedSelectionKeys, setLockedSelectionKeys] = useState<React.Key[]>([]);
    // 按 key 缓存选中的行数据，用于批量操作
    const [selectionCache, setSelectionCache] = useState<Map<React.Key, OrderType>>(new Map());
    const [selectedRows, setSelectedRows] = useState<OrderType[]>([]);
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC' | null>(null);

    // 默认时间范围不写入 URL：只在页面加载时计算一次，避免每次 render 都生成新时间导致无限 refetch
    const defaultOrderTime = useMemo(() => getLastWeekDateTimeRange(), []);

    const getEffectiveOrderTime = (orderTime: any) => {
        // null：用户手动清空（点击叉号），保持为空，不回填默认值
        if (orderTime === null) return null;
        const isValid = orderTime &&
            Array.isArray(orderTime) &&
            orderTime.length === 2 &&
            orderTime[0] !== null &&
            orderTime[1] !== null;
        return isValid ? orderTime : defaultOrderTime;
    };

    // 必填项参数处理
    // 如果有 currentWarehouseId，始终使用 currentWarehouse.sortingCode 作为 sortingCode（用于数据隔离）
    // 即使有分拣码段，也传递 sortingCode 给后端，后端会用于数据隔离（AND条件）
    const handleRequiredFields = (value: any): WMSOrderFiltersType => {
        const warehouseId = currentWarehouse?.id || value.currentWarehouseId;
        // 检查 currentWarehouse 和 warehouseId
        if (!warehouseId || warehouseId === "0") {
            console.warn("⚠️ [WmsParcelsPage] currentWarehouseId 为空或为 0:", {
                currentWarehouseId: warehouseId,
                currentWarehouse: currentWarehouse,
                valueCurrentWarehouseId: value.currentWarehouseId
            });
        }
        // 归一化逻辑与「下载」入口共用，避免两处漂移
        return normalizeRequiredFields(value, currentWarehouse);
    };

    // 使用自定义hook获取数据
    // 默认时间范围不写入 URL：只在查询层补齐默认值
    const normalizedFilters = handleRequiredFields({
        ...filters,
        orderTime: getEffectiveOrderTime(filters.orderTime)
    });

    const {
        parcelData,
        loading,
        refetch,
        totalElements,
        accessiblePages,
        buildQueryVariables
    } = useWmsParcelQuery(normalizedFilters, currentPage, pageSize, sortField, sortDirection);

    // 查不到时引导到历史包裹查询（基于本次已生效的查询条件）
    const queryRangeDays = useParcelQueryRangeDays();
    const missingTrackingNumbers = useNotFoundTrackingNumbers({
        trackingNumbers: filters.trackingNumbers,
        totalElements,
        loading,
        query: GET_ORDER_LISTING_FOR_WMS,
        dataKey: 'getOrderListingForWMS',
        variables: buildQueryVariables(),
    });
    const showParcelEmptyGuide = !loading
        && (parcelData?.length ?? 0) === 0
        && Number(totalElements) === 0
        && (hasTrackingOrOrderNumber(filters) || hasTrackingOrOrderNumber(tempFilters));
    const emptyGuideFilters = hasTrackingOrOrderNumber(filters) ? filters : tempFilters;

    // 勾选变更时：同时维护当前页受控选中 key + 跨页选中状态
    const handleSelectChange = (newSelectedRowKeys: React.Key[], currentPageRows: OrderType[]) => {
        // 1) 更新当前页受控选中 key
        setSelectedRowKeys(newSelectedRowKeys);

        // 2) 合并更新跨页选中 key 集合
        setLockedSelectionKeys(prevKeys => {
            const preserved = new Set<React.Key>(prevKeys);
            // 当前页涉及到的 key 集合
            const pageKeys = new Set<React.Key>();
            parcelData.forEach(order => {
                const key: React.Key = order.trackingNumber || order.id;
                pageKeys.add(key);
                if (newSelectedRowKeys.includes(key)) {
                    preserved.add(key);
                } else {
                    // 仅在当前页出现但未勾选的 key 需要从集合中移除
                    preserved.delete(key);
                }
            });

            // 兜底：确保回调传入的新选中行也在集合中
            currentPageRows.forEach(row => {
                const key: React.Key = row.trackingNumber || row.id;
                if (newSelectedRowKeys.includes(key)) {
                    preserved.add(key);
                }
            });

            return Array.from(preserved);
        });

        // 3) 基于跨页 key 和当前页数据，维护选中行缓存
        setSelectionCache(prevMap => {
            const next = new Map(prevMap);
            const newKeySet = new Set<React.Key>(newSelectedRowKeys);

            // 先根据当前页数据增删缓存
            parcelData.forEach(order => {
                const key: React.Key = order.trackingNumber || order.id;
                if (newKeySet.has(key)) {
                    next.set(key, order);
                } else {
                    // 只移除当前页中取消勾选的记录，其它页的缓存不动
                    if (next.has(key)) {
                        next.delete(key);
                    }
                }
            });

            // 确保回调给的行也在缓存中（应对不在 parcelData 中但仍在选中集合里的情况）
            currentPageRows.forEach(row => {
                const key: React.Key = row.trackingNumber || row.id;
                if (newKeySet.has(key)) {
                    next.set(key, row);
                }
            });

            return next;
        });
    };

    const handleResetFilters = () => {
        const initialFilters = createInitialFilters();
        // updateHashState 会保留 undefined 字段，encodeHash 会过滤它们，URL hash 中不会包含
        setTempFilters(initialFilters);
        setFilters(initialFilters);
    };

    const handleFilter = () => {
        // 直接使用 tempFilters 的值来更新 filters，确保 hashState 同步
        const newFilters = handleRequiredFields(tempFilters);
        // 需要明确设置所有字段，包括要删除的字段（设为 undefined）
        // 这样 updateHashState 才能包含这些字段，encodeHash 会过滤它们，URL hash 中不会包含
        // 如果 newFilters 中某个字段不存在，但 filters 中有，需要明确设置为 undefined
        // 这样可以确保旧字段被包含在更新中，encodeHash 会过滤它们
        const allKeys = new Set([...Object.keys(newFilters), ...Object.keys(filters)]);
        allKeys.forEach(key => {
            if (!(key in newFilters)) {
                (newFilters as any)[key] = undefined;
            }
        });
        // updateHashState 会保留 undefined 字段，encodeHash 会过滤它们，URL hash 中不会包含已删除的字段
        setFilters(newFilters);
        setCurrentPage(1);
    };

    // 根据条件变更刷新数据
    useEffect(() => {
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize, sortField, sortDirection, currentWarehouse?.id]);

    // 当筛选条件、站点或排序变化时，重置所有与选择相关的状态
    useEffect(() => {
        setSelectedRowKeys([]);
        setLockedSelectionKeys([]);
        setSelectionCache(new Map());
        setSelectedRows([]);
    }, [filters, currentWarehouse?.id, sortField, sortDirection]);

    // 数据刷新后，根据跨页 key 集合恢复当前页勾选状态
    useEffect(() => {
        if (loading || !parcelData || parcelData.length === 0) {
            return;
        }

        const keysOnThisPage: React.Key[] = [];
        parcelData.forEach(order => {
            const key: React.Key = order.trackingNumber || order.id;
            if (lockedSelectionKeys.includes(key)) {
                keysOnThisPage.push(key);
            }
        });
        setSelectedRowKeys(keysOnThisPage);
    }, [parcelData, loading, lockedSelectionKeys]);

    // 每次缓存 Map 更新时，同步导出选中行数组，供筛选区和操作按钮使用
    useEffect(() => {
        setSelectedRows(Array.from(selectionCache.values()));
    }, [selectionCache]);

    const showParcelDetail = (record: OrderType) => {
        setOrderId(record.id);
        setDetailVisible(true);
    };
    
    // 处理分页变化：只更新分页参数，勾选行为由 lockedSelectionKeys + 上面的 effect 统一恢复
    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* 筛选条件 */}
            <WmsParcelFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                handleFilter={handleFilter}
                    handleResetFilters={handleResetFilters}
                selectedRows={selectedRows}
            />

            {/* 部分运单未查到提示 */}
            {queryRangeDays != null && (
                <PartialNotFoundBar
                    system="WMS"
                    days={queryRangeDays}
                    missingTrackingNumbers={missingTrackingNumbers}
                    filters={filters}
                />
            )}

            {/* 订单列表 / 空态引导（直接渲染，不依赖 Table locale.emptyText） */}
            {showParcelEmptyGuide && queryRangeDays != null ? (
                <div className="mb-4 py-6 flex justify-center">
                    <ParcelEmptyGuide system="WMS" days={queryRangeDays} filters={emptyGuideFilters} />
                </div>
            ) : (
                <ParcelColumns
                    parcelData={parcelData}
                    showParcelDetail={showParcelDetail}
                    onSelectChange={handleSelectChange}
                    selectedRowKeys={selectedRowKeys}
                    loading={loading}
                    onSortChange={(field, direction) => {
                        setSortField(field ?? null);
                        setSortDirection(direction ?? null);
                        setCurrentPage(1);
                    }}
                    sortField={sortField}
                    sortDirection={sortDirection}
                />
            )}

            {/* 分页：total 显示真实总数，maxPages 限制最大可翻页数（深度翻页保护）*/}
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
                maxPages={accessiblePages}
            />

            {/* 包裹详情抽屉组件 */}
            <CentralParcelDetailDrawer
                showInterceptionExemptionAlert={true}
                visible={detailVisible}
                onClose={() => setDetailVisible(false)}
                orderId={orderId}
                userRole={getUserInfo()?.account?.role?.name || ''}
                onRefreshList={() => refetch(buildQueryVariables())}
                showExternalTrack={true}
            />
        </div>
    );
};

export default WmsParcelsPage;